import { useState, useRef, useEffect, useId } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils/cn";

export interface BulkAction {
  id: string;
  label: string;
  disabled?: boolean;
  title?: string;
}

interface BulkActionMenuProps {
  actions: BulkAction[];
  onAction: (id: string) => void;
  disabled?: boolean;
}

/**
 * Accessible dropdown button for Bulk Actions on the Draft Calendar.
 * Converts TelerikDropDownButton → native <button> + listbox.
 */
export function BulkActionMenu({ actions, onAction, disabled }: BulkActionMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // Close on outside click
  useEffect(() => {
    function handler(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, []);

  function getMenuItems() {
    return [
      ...(containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ?? [])
    ];
  }

  function handleKey(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      if (e.key === "ArrowDown") {
        requestAnimationFrame(() => getMenuItems()[0]?.focus());
      }
    }
  }

  function handleMenuKeyDown(e: React.KeyboardEvent<HTMLUListElement>) {
    const items = getMenuItems();
    const idx = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleKey}
        className={cn(
          "inline-flex items-center gap-1 border border-slate-300 px-3 py-1.5 text-sm font-medium",
          "hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
        )}
      >
        Bulk Action
        <ChevronDown className="h-3.5 w-3.5" />
      </button>

      {open && (
        <ul
          id={menuId}
          role="menu"
          onKeyDown={handleMenuKeyDown}
          className="absolute left-0 top-full z-50 mt-1 w-48 rounded border border-slate-200 bg-white shadow-lg"
        >
          {actions.map((action) => (
            <li key={action.id} role="none">
              <button
                type="button"
                role="menuitem"
                disabled={action.disabled}
                title={action.title}
                onClick={() => { onAction(action.id); setOpen(false); }}
                className={cn(
                  "w-full px-4 py-2 text-left text-sm hover:bg-slate-50",
                  action.disabled && "cursor-not-allowed opacity-40"
                )}
              >
                {action.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
